import {
  computed,
  DestroyRef,
  inject,
  Injectable,
  signal,
} from '@angular/core';
import { Beneficjent } from '../../shared/models/beneficjent';
import { SessionCounterService } from './session-counter.service';
import { APP_PATHS } from '../config/app-paths';
import { Router } from '@angular/router';
import {
  catchError,
  EMPTY,
  finalize,
  map,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { AuthApi } from '../../shared/services/api/auth.api';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { SESSION_HEADER_NAME } from '../config/app-config';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SpinnerService } from './spinner.service';
import { ApplicationContextService } from './application-context.service';
import { Organizacja } from '../../shared/models/organizacja';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { VerifyUserDataDialog } from '../../shared/components/verify-user-data-dialog/verify-user-data-dialog';
import { LastUsedApplicationService } from './last-used-application.service';
import {
  HttpResponseErrorSource,
  HttpResponseService,
} from './http-error-response.service';
import { OrganizacjaApi } from '../../shared/services/api/organizacja.api';
import { AssetsService } from '../../shared/services/assets.service';
import { InfoDialog } from '../../shared/components/info-dialog/info-dialog';
import { UserApi } from '../../shared/services/api/user.api';
import { BeneficjentRole } from '../../shared/models/beneficjent-role';
import { Application } from '../../shared/models/application';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  readonly #destroyRef = inject(DestroyRef);
  readonly #router = inject(Router);
  readonly #authApi = inject(AuthApi);
  readonly #userApi = inject(UserApi);
  readonly #spinnerService = inject(SpinnerService);
  readonly #sessionService = inject(SessionCounterService);
  readonly #dialogService = inject(DialogService);
  readonly #lastUsedApplicationService = inject(LastUsedApplicationService);
  readonly #httpResponseService = inject(HttpResponseService);
  readonly #loggedUser = signal<Beneficjent | null>(null);
  readonly hasSession = computed(() => !!this.#loggedUser());
  readonly #organizacjaApi = inject(OrganizacjaApi);
  readonly dialogService = inject(DialogService);
  private verifyUserDataDialog: DynamicDialogRef<VerifyUserDataDialog> | null =
    null;
  readonly #assetsService = inject(AssetsService);

  #announcemenctsEmailNotificationsDialog: DynamicDialogRef<InfoDialog> | null =
    null;
  readonly #applicationContextService = inject(ApplicationContextService);

  constructor() {
    this.#subscribeToEndSession();
  }

  public getUser(): Beneficjent | null {
    return this.#loggedUser();
  }

  public setUser(user: Beneficjent | null): void {
    this.#loggedUser.set(user);
  }

  public getSelectedOrganization(): Organizacja | null {
    return this.#applicationContextService.getOrganizationFromContext();
  }

  public signInByLogin(username: string, password: string): void {
    this.#authApi
      .signInByLogin$(username, password)
      .pipe(
        switchMap((response: HttpResponse<Beneficjent>) => {
          const beneficjent = response.body;

          if (beneficjent && beneficjent.id != null) {
            return this.joinOrganizationAndGetUserData$().pipe(
              map((updatedUser) => {
                if (updatedUser) {
                  return response.clone({ body: updatedUser });
                }
                return response;
              }),
            );
          }

          return of(response);
        }),
        tap((finalResponse: HttpResponse<Beneficjent>) => {
          const beneficjent = finalResponse.body;

          if (beneficjent && beneficjent.id != null) {
            this.#successLogin(finalResponse);
          } else {
            this.#router.navigate(
              [APP_PATHS.auth.root, APP_PATHS.auth.firstLogin],
              { state: { data: beneficjent } },
            );
            this.#spinnerService.hide();
          }
        }),
        catchError((err: HttpErrorResponse) => {
          this.#errorLogin(err, err?.error?.message || 'Niepoprawny login lub hasło');
          return EMPTY;
        }),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  public signInBySignature(sign: string): void {
    this.#authApi
      .signInBySignature$(sign)
      .pipe(
        switchMap((response: HttpResponse<Beneficjent>) => {
          const beneficjent = response.body;
          if (beneficjent && beneficjent.id != null) {
            return this.joinOrganizationAndGetUserData$().pipe(
              map((updatedUser) => {
                if (updatedUser) {
                  return response.clone({ body: updatedUser });
                }
                return response;
              }),
            );
          }

          return of(response);
        }),
        tap((finalResponse: HttpResponse<Beneficjent>) => {
          const beneficjent = finalResponse.body;
          if (beneficjent && beneficjent.id != null) {
            this.#successLogin(finalResponse);
          } else {
            this.#router.navigate(
              [APP_PATHS.auth.root, APP_PATHS.auth.firstLogin],
              { state: { data: beneficjent } },
            );
            this.#spinnerService.hide();
          }
        }),
        catchError((err) => {
          this.#errorLogin(err, 'Błąd logowania');
          return EMPTY;
        }),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  public refreshSession(): void {
    this.#authApi
      .refreshSession$()
      .pipe(
        tap(() => this.#sessionService.refreshSessionCounter()),
        catchError((err) => {
          this.#httpResponseService.showErrorMessage(
            err,
            'Wystąpił błąd podczas próby odświeżenia sesji',
          );
          return EMPTY;
        }),
      )
      .subscribe();
  }

  #endSession(): void {
    this.dialogService.dialogComponentRefMap.forEach((componentRef) => {
      componentRef.instance.close();
    });
    if (this.#sessionService.tryAcquireLogoutLock()) {
      this.#spinnerService.show();
      this.#authApi
        .logOut$()
        .pipe(
          tap(() => {
            this.#loggedUser.set(null);
            this.#router.navigate([
              APP_PATHS.auth.root,
              APP_PATHS.auth.loginMethod,
            ]);
            this.#sessionService.endSessionSessionChannelPost();
          }),
          catchError((err) => {
            this.#httpResponseService.showErrorMessage(
              err,
              'Wystąpił błąd podczas próby wylogowania',
            );
            return EMPTY;
          }),
          finalize(() => {
            this.#spinnerService.hide();
          }),
          takeUntilDestroyed(this.#destroyRef),
        )
        .subscribe();
    } else {
      this.#loggedUser.set(null);
      this.#router.navigate([APP_PATHS.auth.root, APP_PATHS.auth.loginMethod]);
    }
  }

  public checkAndHandleSession$(): Observable<boolean> {
    if (this.hasSession()) {
      return of(true);
    } else {
      return this.#getUserFromSession$().pipe(
        switchMap((response: HttpResponse<Beneficjent>) => {
          const beneficjent = response.body;

          if (beneficjent && beneficjent.id != null) {
            return this.joinOrganizationAndGetUserData$().pipe(
              map((updatedUser) => {
                if (updatedUser) {
                  return response.clone({ body: updatedUser });
                }
                return response;
              }),
            );
          }
          return of(response);
        }),
        tap((finalResponse: HttpResponse<Beneficjent>) => {
          this.#startSession(finalResponse);
        }),
        map(() => this.hasSession()),
        catchError(() => of(false)),
      );
    }
  }

  public checkAndHandleAdminSession$(): Observable<boolean> {
    if (this.hasSession()) {
      return of(this.isUserAdmin());
    } else {
      return this.#getUserFromSession$().pipe(
        tap((finalResponse: HttpResponse<Beneficjent>) => {
          this.#startSession(finalResponse);
        }),
        map(() => this.isUserAdmin()),
        catchError(() => of(false)),
      );
    }
  }

  public checkSession$(): Observable<boolean> {
    return this.#getUserFromSession$().pipe(
      map((response: HttpResponse<Beneficjent>) => {
        const beneficjent = response.body;
        return !!beneficjent?.id;
      }),
      catchError(() => of(false)),
    );
  }

  public joinOrganizationAndGetUserData$(): Observable<Beneficjent | null> {
    return this.#organizacjaApi.joinOrganizationByIdentity$().pipe(
      tap((count: number) => {
        if (count > 0) {
          this.#httpResponseService.showSuccessMessage(
            'Pomyślnie dołączono do organizacji',
          );
        }
      }),
      switchMap((count: number) => {
        if (count > 0) {
          return this.#authApi
            .getCurrentUser$()
            .pipe(map((response) => response.body));
        }
        return of(null);
      }),
      catchError((error: HttpErrorResponse) => {
        this.#httpResponseService.showErrorMessage(
          error,
          'Błąd podczas aktualizacji danych organizacji',
          HttpResponseErrorSource.MESSAGE_FROM_RESPONSE,
        );
        return of(null);
      }),
    );
  }

  #successLogin(response: HttpResponse<Beneficjent>): void {
    this.#startSession(response);
    this.#updateLastLoginDate();
    this.#httpResponseService.showSuccessMessage('Poprawne dane logowania');
    this.#spinnerService.hide();

    const loggedUser = this.#loggedUser();
    if (!loggedUser?.organizacje?.length && !this.isUserAdmin()) {
      this.#router.navigate([APP_PATHS.home]);
    } else {
      this.#router.navigate([
        APP_PATHS.auth.root,
        APP_PATHS.auth.applicationContext,
      ]);
    }
  }

  #errorLogin(err: HttpErrorResponse, errorMessage: string): void {
    this.#spinnerService.hide();
    this.#httpResponseService.showErrorMessage(
      err,
      errorMessage,
      HttpResponseErrorSource.MESSAGE_FROM_ERROR_CODE,
    );
  }

  #startSession(response: HttpResponse<Beneficjent>): void {
    const sessionDuration = response.headers.get(SESSION_HEADER_NAME);
    this.#sessionService.startSessionCounter(Number(sessionDuration));

    this.#refreshCachedAsset();

    const user = response?.body;
    this.#loggedUser.set(user);

    const loggedUser = this.#loggedUser();

    const lastUsedApplications = loggedUser?.ostatnioSkaldaneWnioski;
    if (lastUsedApplications?.length) {
      this.#lastUsedApplicationService.appCodes.set(
        lastUsedApplications as Application[],
      );
    }

    this.#applicationContextService.handleContextOnInit(loggedUser!);
  }

  #refreshCachedAsset(): void {
    this.#assetsService
      .refreshJson('static/ewnioski/texts/resources.json')
      .pipe(
        catchError(() => EMPTY),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  #getUserFromSession$(): Observable<HttpResponse<Beneficjent>> {
    return this.#authApi.getCurrentUser$();
  }

  #subscribeToEndSession(): void {
    this.#sessionService.endSession$
      .pipe(
        tap(() => this.#endSession()),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  #updateLastLoginDate(): void {
    this.#authApi
      .updateLastLoginDate$()
      .pipe(
        tap((result: boolean) => {
          if (result) {
            this.updateUserDataDialog();
          }
        }),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  public updateUserDataDialog(): void {
    if (this.verifyUserDataDialog) {
      return;
    }

    this.verifyUserDataDialog = this.#dialogService.open(VerifyUserDataDialog, {
      closable: false,
      showHeader: false,
      closeOnEscape: false,
      modal: true,
      dismissableMask: false,
      width: '65vw',
      inputValues: {
        beneficiary: this.getUser(),
      },
      breakpoints: {
        '960px': '70vw',
        '640px': '90vw',
      },
    });

    if (this.verifyUserDataDialog) {
      this.verifyUserDataDialog.onClose
        .pipe(
          tap((result) => {
            if (result === 'edit-data') {
              this.#navigateToEditUser();
            }
          }),
          finalize(() => {
            this.verifyUserDataDialog = null;
          }),
          takeUntilDestroyed(this.#destroyRef),
        )
        .subscribe();
    }
  }

  #navigateToEditUser(): void {
    if (this.#applicationContextService.isOrganizationContext()) {
      this.#applicationContextService.switchToBeneficiary();
    }

    const url = `${APP_PATHS.userSettings.root}/${APP_PATHS.userSettings.editUser}`;
    this.#router.navigate([url]);
  }

  public checkTermsAndConditions$(): Observable<boolean> {
    if (this.#loggedUser() && !!this.#loggedUser()?.zmianaRegulaminu) {
      return of(true);
    }
    return of(false);
  }

  public showAnnouncemenctsEmailNotificationsDialog(): void {
    this.#announcemenctsEmailNotificationsDialog = this.#dialogService.open(
      InfoDialog,
      {
        closable: false,
        showHeader: false,
        closeOnEscape: false,
        modal: true,
        dismissableMask: false,
        width: '40vw',
        inputValues: {
          header: 'Informacja',
          content:
            'W celu wyrażenia zgody na otrzymywanie komunikatów drogą komunikacji elektronicznej przejdź do Panelu użytkownika.',
          hideCancelButton: true,
        },
        breakpoints: {
          '960px': '40vw',
          '640px': '90vw',
        },
      },
    );

    // jeśli nie ma w bazie informacji o notyfikacjach email dla komunikatów
    // to dodajemy wpis z domyślnie wyłączonymi powiadomieniami
    // dzięki temu wiemy komu wyświetlić komunikat po zalogowaniu
    if (this.#announcemenctsEmailNotificationsDialog) {
      this.#announcemenctsEmailNotificationsDialog.onClose
        .pipe(
          switchMap(() =>
            this.#userApi.announcementsEmailConsent$().pipe(
              catchError((err) => {
                this.#httpResponseService.showErrorMessage(
                  err,
                  'Nie udało się zaktualizować danych beneficjenta',
                );
                return EMPTY;
              }),
            ),
          ),
          tap(() => this.#navigateToUserAccountSettings()),
          takeUntilDestroyed(this.#destroyRef),
        )
        .subscribe();
    }
  }

  public isUserAdmin(): boolean {
    return (
      this.getUser()?.role?.some(
        (value) => value === BeneficjentRole.ADMINISTRATOR,
      ) || false
    );
  }

  #navigateToUserAccountSettings(): void {
    if (this.#applicationContextService.isOrganizationContext()) {
      this.#applicationContextService.switchToBeneficiary();
    }

    const url = `${APP_PATHS.userSettings.root}`;
    this.#router.navigate([url], { queryParams: { tab: '2' } });
  }
}
